import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormFeedback,
  Label,
  Badge,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import DeleteModal from "../../Components/Common/DeleteModal";
import TableContainer from "../../Components/Common/TableContainer";
import Loader from "../../Components/Common/Loader";

import api from "../../utills/api";

import { useNavigate } from "react-router-dom";

/* ============================================================
   HELPERS
============================================================ */

const getToken = () => {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    ""
  );
};

const getAuthHeaders = () => {
  const token = getToken();

  return {
    "Content-Type": "application/json",
    ...(token
      ? {
        Authorization: `Bearer ${token}`,
      }
      : {}),
  };
};

/**
 * Normalize API response.
 *
 * Supports:
 * {
 *   success: true,
 *   data: {
 *     leads: []
 *   }
 * }
 *
 * {
 *   success: true,
 *   data: []
 * }
 *
 * {
 *   leads: []
 * }
 */
const getResponseData = (response) => {
  if (!response) return [];

  if (
    response.data &&
    Array.isArray(response.data.leads)
  ) {
    return response.data.leads;
  }

  if (Array.isArray(response.leads)) {
    return response.leads;
  }

  if (Array.isArray(response.data)) {
    return response.data;
  }

  return [];
};

const getLeadName = (lead) => {
  if (lead?.name) {
    return lead.name;
  }

  if (lead?.studentName) {
    return lead.studentName;
  }

  return "Unnamed Student";
};

const formatDate = (date) => {
  if (!date) return "-";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }

  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (date) => {
  if (!date) return "-";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return "-";
  }

  return parsed.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatusClass = (status) => {
  switch (
  String(status || "").toLowerCase()
  ) {
    case "new":
      return "bg-primary-subtle text-primary";

    case "contacted":
      return "bg-info-subtle text-info";

    case "follow-up":
      return "bg-warning-subtle text-warning";

    case "qualified":
      return "bg-success-subtle text-success";

    case "counselling":
      return "bg-secondary-subtle text-secondary";

    case "application":
      return "bg-info-subtle text-info";

    case "admitted":
      return "bg-success-subtle text-success";

    case "lost":
      return "bg-danger-subtle text-danger";

    default:
      return "bg-light text-body";
  }
};

const getPriorityClass = (priority) => {
  switch (
  String(priority || "").toLowerCase()
  ) {
    case "high":
      return "bg-danger-subtle text-danger";

    case "medium":
      return "bg-warning-subtle text-warning";

    case "low":
      return "bg-success-subtle text-success";

    default:
      return "bg-light text-body";
  }
};

/* ============================================================
   OPTIONS
============================================================ */

const sourceOptions = [
  { label: "Website", value: "Website" },
  { label: "Meta", value: "Meta" },
  { label: "Facebook", value: "Facebook" },
  { label: "Instagram", value: "Instagram" },
  { label: "Google", value: "Google" },
  { label: "YouTube", value: "YouTube" },
  { label: "WhatsApp", value: "WhatsApp" },
  { label: "Phone", value: "Phone" },
  { label: "Walk-in", value: "Walk-in" },
  { label: "Referral", value: "Referral" },
  { label: "Other", value: "Other" },
];

const statusOptions = [
  { label: "New", value: "New" },
  { label: "Contacted", value: "Contacted" },
  { label: "Follow-up", value: "Follow-up" },
  { label: "Qualified", value: "Qualified" },
  { label: "Counselling", value: "Counselling" },
  { label: "Application", value: "Application" },
  { label: "Admitted", value: "Admitted" },
  { label: "Lost", value: "Lost" },
];

const priorityOptions = [
  { label: "Low", value: "Low" },
  { label: "Medium", value: "Medium" },
  { label: "High", value: "High" },
];



/* ============================================================
   COMPONENT
============================================================ */

const AllLeads = () => {

  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);

  const [selectedLead, setSelectedLead] =
    useState(null);

  const [selectedLeadDetails, setSelectedLeadDetails] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [detailsLoading, setDetailsLoading] =
    useState(false);

  const [error, setError] = useState("");

  const [modal, setModal] =
    useState(false);

  const [isEdit, setIsEdit] =
    useState(false);

  const [deleteModal, setDeleteModal] =
    useState(false);

  const [organizationCourses, setOrganizationCourses] = useState([]);

  const [deleteModalMulti, setDeleteModalMulti] =
    useState(false);

  const [selectedIds, setSelectedIds] =
    useState([]);

  const [filterModal, setFilterModal] =
    useState(false);

  const [filters, setFilters] =
    useState({
      status: "",
      priority: "",
      source: "",
      search: "",
    });


  /* ============================================================
     FETCH ALL LEADS
  ============================================================ */

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await api.get("/leads", {
        headers: getAuthHeaders(),
      });

      const result = response.data;
      const data = getResponseData(result);

      setLeads(data);

      // Automatically select first lead
      if (data.length > 0) {
        setSelectedLead((currentSelectedLead) => {
          if (!currentSelectedLead) {
            return data[0];
          }

          const refreshedLead = data.find(
            (item) => item._id === currentSelectedLead._id
          );

          return refreshedLead || currentSelectedLead;
        });
      } else {
        setSelectedLead(null);
        setSelectedLeadDetails(null);
      }
    } catch (err) {
      console.error("Fetch leads error:", err);

      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to fetch leads.";

      setError(message);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
  const loadOrganizationCourses = async () => {
    try {
      const response = await api.get("/organization");
      const data = response?.data?.data || response?.data || {};

      setOrganizationCourses(
        Array.isArray(data.courses) ? data.courses : []
      );
    } catch (error) {
      console.error("Failed to load organization courses:", error);
      setOrganizationCourses([]);
    }
  };

  loadOrganizationCourses();
}, []);

  /* ============================================================
     FETCH SELECTED LEAD DETAILS
  ============================================================ */

  const fetchLeadDetails =
    useCallback(
      async (leadId) => {
        if (!leadId) {
          setSelectedLeadDetails(null);
          return;
        }

        try {
          setDetailsLoading(true);

          const response =
            await api.get(
              `/leads/${leadId}`,
              {
                headers:
                  getAuthHeaders(),
              }
            );

          const result =
            response.data;

          /*
           * Backend returns:
           *
           * {
           *   success: true,
           *   data: {
           *     lead,
           *     profile,
           *     activities
           *   }
           * }
           */

          setSelectedLeadDetails(
            result?.data || null
          );
        } catch (err) {
          console.error(
            "Fetch lead details error:",
            err
          );

          setSelectedLeadDetails(null);

          toast.error(
            err?.response?.data
              ?.message ||
            err?.message ||
            "Unable to load lead details."
          );
        } finally {
          setDetailsLoading(false);
        }
      },
      []
    );

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    if (selectedLead?._id) {
      fetchLeadDetails(
        selectedLead._id
      );
    }
  }, [
    selectedLead?._id,
    fetchLeadDetails,
  ]);

  /* ============================================================
     FILTERED LEADS
  ============================================================ */

  const filteredLeads = useMemo(() => {
    return leads.filter((lead) => {
      const search =
        filters.search
          .trim()
          .toLowerCase();

      const name =
        getLeadName(lead)
          .toLowerCase();

      const email =
        String(
          lead.email || ""
        ).toLowerCase();

      const phone =
        String(
          lead.phone || ""
        ).toLowerCase();

      const leadNumber =
        String(
          lead.leadNumber || ""
        ).toLowerCase();

      const matchesSearch =
        !search ||
        name.includes(search) ||
        email.includes(search) ||
        phone.includes(search) ||
        leadNumber.includes(search);

      const matchesStatus =
        !filters.status ||
        String(
          lead.status || ""
        ).toLowerCase() ===
        filters.status.toLowerCase();

      const matchesPriority =
        !filters.priority ||
        String(
          lead.priority || ""
        ).toLowerCase() ===
        filters.priority.toLowerCase();

      const matchesSource =
        !filters.source ||
        String(
          lead.source || ""
        ).toLowerCase() ===
        filters.source.toLowerCase();

      return (
        matchesSearch &&
        matchesStatus &&
        matchesPriority &&
        matchesSource
      );
    });
  }, [leads, filters]);

  /* ============================================================
     SELECT LEAD
  ============================================================ */

  const handleSelectLead = (lead) => {
    if (!lead?._id) {
      console.error("Lead ID is missing:", lead);
      return;
    }

    navigate(`/leads/${lead._id}`);
  };
  /* ============================================================
     ADD / EDIT
  ============================================================ */

  const handleAddLead = () => {
    setSelectedLead(null);
    setIsEdit(false);
    setModal(true);
  };

  const handleEditLead = (lead) => {
    setSelectedLead(lead);
    setIsEdit(true);
    setModal(true);
  };

  const toggleModal = () => {
    setModal(
      (previous) => !previous
    );
  };

  /* ============================================================
     FORM
  ============================================================ */

  const validation = useFormik({
    enableReinitialize: true,

   initialValues: {
  name: isEdit && selectedLead
    ? selectedLead.name || ""
    : "",

  email: isEdit && selectedLead
    ? selectedLead.email || ""
    : "",

  phone: isEdit && selectedLead
    ? selectedLead.phone || ""
    : "",

  alternatePhone: isEdit && selectedLead
    ? selectedLead.alternatePhone || ""
    : "",

  source: isEdit && selectedLead
    ? selectedLead.source || "Website"
    : "Website",

  priority: isEdit && selectedLead
    ? selectedLead.priority || "Medium"
    : "Medium",

  status: isEdit && selectedLead
    ? selectedLead.status || "New"
    : "New",

  notes: isEdit && selectedLead
    ? selectedLead.notes || ""
    : "",

  // Education profile
  course: selectedLeadDetails?.lead?._id === selectedLead?._id
    ? selectedLeadDetails?.profile?.course || ""
    : "",

  program: selectedLeadDetails?.lead?._id === selectedLead?._id
    ? selectedLeadDetails?.profile?.program || ""
    : "",

  specialization: selectedLeadDetails?.lead?._id === selectedLead?._id
    ? selectedLeadDetails?.profile?.specialization || ""
    : "",

  academicYear: selectedLeadDetails?.lead?._id === selectedLead?._id
    ? selectedLeadDetails?.profile?.academicYear || ""
    : "",

  admissionType: selectedLeadDetails?.lead?._id === selectedLead?._id
    ? selectedLeadDetails?.profile?.admissionType || ""
    : "",

  parentName: selectedLeadDetails?.lead?._id === selectedLead?._id
    ? selectedLeadDetails?.profile?.parentName || ""
    : "",

  parentPhone: selectedLeadDetails?.lead?._id === selectedLead?._id
    ? selectedLeadDetails?.profile?.parentPhone || ""
    : "",

  parentEmail: selectedLeadDetails?.lead?._id === selectedLead?._id
    ? selectedLeadDetails?.profile?.parentEmail || ""
    : "",

  parentRelation: selectedLeadDetails?.lead?._id === selectedLead?._id
    ? selectedLeadDetails?.profile?.parentRelation || ""
    : "",

  previousQualification: selectedLeadDetails?.lead?._id === selectedLead?._id
    ? selectedLeadDetails?.profile?.previousQualification || ""
    : "",

  previousSchool: selectedLeadDetails?.lead?._id === selectedLead?._id
    ? selectedLeadDetails?.profile?.previousSchool || ""
    : "",

  previousBoard: selectedLeadDetails?.lead?._id === selectedLead?._id
    ? selectedLeadDetails?.profile?.previousBoard || ""
    : "",

  percentage: selectedLeadDetails?.lead?._id === selectedLead?._id
    ? selectedLeadDetails?.profile?.percentage ?? ""
    : "",

  preferredCampus: selectedLeadDetails?.lead?._id === selectedLead?._id
    ? selectedLeadDetails?.profile?.preferredCampus || ""
    : "",
},

    validationSchema:
      Yup.object({
        name: Yup.string()
          .trim()
          .required(
            "Please enter student name."
          ),

        phone: Yup.string()
          .trim()
          .required(
            "Please enter phone number."
          ),

        source:
          Yup.string().required(
            "Please select lead source."
          ),

        priority:
          Yup.string().required(
            "Please select priority."
          ),

        status:
          Yup.string().required(
            "Please select status."
          ),
      }),

    
onSubmit: async (values, { resetForm }) => {
  try {
    let response;

    // Separate lead fields from education profile fields.
    const {
      course,
      program,
      specialization,
      academicYear,
      admissionType,
      preferredCampus,
      parentName,
      parentPhone,
      parentEmail,
      parentRelation,
      previousQualification,
      previousSchool,
      previousBoard,
      percentage,
      ...leadPayload
    } = values;

    const profilePayload = {
      course,
      program,
      specialization,
      academicYear,
      admissionType,
      preferredCampus,
      parentName,
      parentPhone,
      parentEmail,
      parentRelation,
      previousQualification,
      previousSchool,
      previousBoard,
      percentage:
        percentage === "" ||
        percentage === null ||
        percentage === undefined
          ? undefined
          : Number(percentage),
    };

    const headers = {
      headers: getAuthHeaders(),
    };

      if (!isEdit) {
        // CREATE LEAD + EDUCATION PROFILE
        response = await api.post(
          "/leads",
          {
            ...leadPayload,
            profile: profilePayload,
          },
          headers
        );
      } else {
        // UPDATE MAIN LEAD DETAILS
        response = await api.put(
          `/leads/${selectedLead?._id}`,
          leadPayload,
          headers
        );

      // UPDATE EDUCATION PROFILE
      await api.put(
        `/leads/${selectedLead?._id}/profile`,
        profilePayload,
        headers
      );
    }

    const result = response.data;

    toast.success(
      isEdit
        ? "Lead updated successfully."
        : "Lead created successfully."
    );

    resetForm();
    setModal(false);

    const updatedLead =
      result?.data?.lead ||
      result?.data?.profile?.lead ||
      result?.data ||
      null;

    await fetchLeads();

    if (updatedLead?._id) {
      setSelectedLead(updatedLead);
      await fetchLeadDetails(updatedLead._id);
    }
  } catch (err) {
    console.error("Lead save error:", err);

    toast.error(
      err?.response?.data?.message ||
        err?.message ||
        (isEdit
          ? "Unable to update lead."
          : "Unable to create lead.")
    );
  }
},

  });

  /* ============================================================
     DELETE SINGLE LEAD
  ============================================================ */

  const handleDeleteLead =
    async () => {
      if (!selectedLead?._id) {
        return;
      }

      try {
        const response =
          await api.delete(
            `/leads/${selectedLead._id}`,
            {
              headers:
                getAuthHeaders(),
            }
          );

        const result =
          response.data;

        toast.success(
          result?.message ||
          "Lead deleted successfully."
        );

        setDeleteModal(false);
        setSelectedLead(null);
        setSelectedLeadDetails(null);

        await fetchLeads();
      } catch (err) {
        console.error(
          "Delete lead error:",
          err
        );

        toast.error(
          err?.response?.data?.message ||
          err?.message ||
          "Unable to delete lead."
        );
      }
    };

  /* ============================================================
     CHECKBOXES
  ============================================================ */

  const handleSelectAll = (
    event
  ) => {
    if (event.target.checked) {
      const ids =
        filteredLeads
          .map(
            (lead) =>
              lead._id
          )
          .filter(Boolean);

      setSelectedIds(ids);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (
    id
  ) => {
    setSelectedIds(
      (previous) => {
        if (
          previous.includes(id)
        ) {
          return previous.filter(
            (item) =>
              item !== id
          );
        }

        return [
          ...previous,
          id,
        ];
      }
    );
  };

  /* ============================================================
     BULK DELETE
  ============================================================ */

  const deleteMultiple =
    async () => {
      if (!selectedIds.length) {
        return;
      }

      try {
        /*
         * Use Axios for every delete.
         * This ensures requests go through
         * the configured /api base URL.
         */
        for (const id of selectedIds) {
          await api.delete(
            `/leads/${id}`,
            {
              headers:
                getAuthHeaders(),
            }
          );
        }

        toast.success(
          "Selected leads deleted."
        );

        setSelectedIds([]);
        setDeleteModalMulti(false);
        setSelectedLead(null);
        setSelectedLeadDetails(null);

        await fetchLeads();
      } catch (err) {
        console.error(
          "Bulk delete error:",
          err
        );

        toast.error(
          err?.response?.data?.message ||
          err?.message ||
          "Unable to delete selected leads."
        );
      }
    };

  /* ============================================================
     FILTERS
  ============================================================ */

  const clearFilters = () => {
    setFilters({
      status: "",
      priority: "",
      source: "",
      search: "",
    });
  };

  /* ============================================================
     TABLE COLUMNS
  ============================================================ */

  const columns = useMemo(
    () => [
      {
        header: (
          <Input
            type="checkbox"
            className="form-check-input"
            checked={
              filteredLeads.length >
              0 &&
              selectedIds.length ===
              filteredLeads.length
            }
            onChange={
              handleSelectAll
            }
          />
        ),

        id: "select",

        enableColumnFilter: false,
        enableSorting: false,

        cell: ({
          row,
        }) => {
          const lead =
            row.original;

          return (
            <Input
              type="checkbox"
              className="form-check-input"
              checked={selectedIds.includes(
                lead._id
              )}
              onChange={() =>
                handleSelectOne(
                  lead._id
                )
              }
              onClick={(e) =>
                e.stopPropagation()
              }
            />
          );
        },
      },

      {
        header: "Lead",

        accessorKey:
          "leadNumber",

        enableColumnFilter: false,

        cell: ({
          row,
        }) => {
          const lead =
            row.original;

          const isSelected =
            selectedLead?._id ===
            lead._id;

          return (
            <div
              onClick={() =>
                handleSelectLead(
                  lead
                )
              }
              style={{
                cursor:
                  "pointer",
              }}
            >
              <div
                className={`fw-semibold ${isSelected
                    ? "text-primary"
                    : "text-dark"
                  }`}
              >
                {lead.leadNumber ||
                  "—"}
              </div>

              <div className="mt-1">
                {getLeadName(
                  lead
                )}
              </div>

              {lead.email && (
                <small className="text-muted">
                  {lead.email}
                </small>
              )}
            </div>
          );
        },
      },

      {
        header: "Phone",

        accessorKey: "phone",

        enableColumnFilter: false,

        cell: ({
          row,
        }) => {
          const lead =
            row.original;

          return (
            <span
              onClick={() =>
                handleSelectLead(
                  lead
                )
              }
              style={{
                cursor:
                  "pointer",
              }}
            >
              {lead.phone ||
                "—"}
            </span>
          );
        },
      },

      {
        header: "Source",

        accessorKey: "source",

        enableColumnFilter: false,

        cell: ({
          row,
        }) => {
          const lead =
            row.original;

          return (
            <Badge
              color="primary"
              className="bg-primary-subtle text-primary"
              style={{
                cursor:
                  "pointer",
              }}
              onClick={() =>
                handleSelectLead(
                  lead
                )
              }
            >
              {lead.source ||
                "—"}
            </Badge>
          );
        },
      },

      {
        header: "Status",

        accessorKey: "status",

        enableColumnFilter: false,

        cell: ({
          row,
        }) => {
          const lead =
            row.original;

          return (
            <span
              className={`badge ${getStatusClass(
                lead.status
              )}`}
              style={{
                cursor:
                  "pointer",
              }}
              onClick={() =>
                handleSelectLead(
                  lead
                )
              }
            >
              {lead.status ||
                "—"}
            </span>
          );
        },
      },

      {
        header: "Priority",

        accessorKey:
          "priority",

        enableColumnFilter: false,

        cell: ({
          row,
        }) => {
          const lead =
            row.original;

          return (
            <span
              className={`badge ${getPriorityClass(
                lead.priority
              )}`}
              style={{
                cursor:
                  "pointer",
              }}
              onClick={() =>
                handleSelectLead(
                  lead
                )
              }
            >
              {lead.priority ||
                "—"}
            </span>
          );
        },
      },

      {
        header: "Action",

        enableColumnFilter: false,

        cell: ({
          row,
        }) => {
          const lead =
            row.original;

          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                tag="button"
                className="btn btn-soft-primary btn-sm"
                onClick={(e) =>
                  e.preventDefault()
                }
              >
                <i className="ri-more-fill" />
              </DropdownToggle>
              <DropdownMenu end>
                <DropdownItem
                  onClick={() => handleSelectLead(lead)}
                >
                  <i className="ri-eye-line me-2" />
                  View Details
                </DropdownItem>

                <DropdownItem
                  onClick={() => handleEditLead(lead)}
                >
                  <i className="ri-pencil-line me-2" />
                  Edit Lead
                </DropdownItem>

                <DropdownItem
                  onClick={() => {
                    setSelectedLead(lead);
                    setDeleteModal(true);
                  }}
                >
                  <i className="ri-delete-bin-line me-2 text-danger" />
                  Delete Lead
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [
      filteredLeads,
      selectedIds,
      selectedLead,
    ]
  );

  /* ============================================================
     PAGE
  ============================================================ */

  document.title =
    "All Leads | eduLeads.Online";

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="All Leads"
            pageTitle="Leads"
          />

          {/* ==================================================
              HEADER
          ================================================== */}

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <div className="flex-grow-1">
                      <h5 className="mb-0">
                        Leads
                      </h5>

                      <small className="text-muted">
                        Manage enquiries,
                        prospects and
                        admissions
                      </small>
                    </div>

                    <div className="hstack gap-2">
                      {selectedIds.length >
                        0 && (
                          <Button
                            color="soft-danger"
                            onClick={() =>
                              setDeleteModalMulti(
                                true
                              )
                            }
                          >
                            <i className="ri-delete-bin-2-line me-1" />
                            Delete
                          </Button>
                        )}

                      <Button
                        color="secondary"
                        onClick={() =>
                          setFilterModal(
                            (previous) =>
                              !previous
                          )
                        }
                      >
                        <i className="ri-filter-2-line me-1" />
                        Filters
                      </Button>

                      <Button
                        color="primary"
                        onClick={
                          handleAddLead
                        }
                      >
                        <i className="ri-add-line me-1" />
                        Add Lead
                      </Button>

                      <Button
                        color="light"
                        onClick={
                          fetchLeads
                        }
                        title="Refresh"
                      >
                        <i className="ri-refresh-line" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            </Col>
          </Row>

          {/* ==================================================
              FILTERS
          ================================================== */}

          {filterModal && (
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody>
                    <Row className="g-3">
                      <Col lg={3}>
                        <Label>
                          Search
                        </Label>

                        <Input
                          value={
                            filters.search
                          }
                          placeholder="Lead number, name, email or phone..."
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              search:
                                e.target
                                  .value,
                            })
                          }
                        />
                      </Col>

                      <Col lg={3}>
                        <Label>
                          Status
                        </Label>

                        <Select
                          isClearable
                          options={
                            statusOptions
                          }
                          placeholder="All Statuses"
                          value={
                            statusOptions.find(
                              (item) =>
                                item.value ===
                                filters.status
                            ) ||
                            null
                          }
                          onChange={(
                            option
                          ) =>
                            setFilters({
                              ...filters,
                              status:
                                option?.value ||
                                "",
                            })
                          }
                        />
                      </Col>

                      <Col lg={3}>
                        <Label>
                          Priority
                        </Label>

                        <Select
                          isClearable
                          options={
                            priorityOptions
                          }
                          placeholder="All Priorities"
                          value={
                            priorityOptions.find(
                              (item) =>
                                item.value ===
                                filters.priority
                            ) ||
                            null
                          }
                          onChange={(
                            option
                          ) =>
                            setFilters({
                              ...filters,
                              priority:
                                option?.value ||
                                "",
                            })
                          }
                        />
                      </Col>

                      <Col lg={3}>
                        <Label>
                          Source
                        </Label>

                        <Select
                          isClearable
                          options={
                            sourceOptions
                          }
                          placeholder="All Sources"
                          value={
                            sourceOptions.find(
                              (item) =>
                                item.value ===
                                filters.source
                            ) ||
                            null
                          }
                          onChange={(
                            option
                          ) =>
                            setFilters({
                              ...filters,
                              source:
                                option?.value ||
                                "",
                            })
                          }
                        />
                      </Col>

                      <Col lg={12}>
                        <Button
                          color="light"
                          onClick={
                            clearFilters
                          }
                        >
                          Clear Filters
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* ==================================================
              MASTER / DETAIL
          ================================================== */}

          <Row>
            {/* ==================================================
                LEFT — LEAD TABLE
            ================================================== */}

            <Col
              xl={8}
              lg={7}
            >
              <Card className="h-100">
                <CardHeader>
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h5 className="card-title mb-1">
                        All Leads
                      </h5>

                      <p className="text-muted mb-0">
                        {filteredLeads.length}{" "}
                        lead
                        {filteredLeads.length !==
                          1
                          ? "s"
                          : ""}
                      </p>
                    </div>

                    {selectedLead && (
                      <Badge
                        color="primary"
                        className="bg-primary-subtle text-primary"
                      >
                        Selected
                      </Badge>
                    )}
                  </div>
                </CardHeader>

                <CardBody className="pt-0">
                  {loading ? (
                    <Loader />
                  ) : error ? (
                    <div className="text-center py-5">
                      <div className="avatar-sm mx-auto mb-3">
                        <div className="avatar-title bg-danger-subtle text-danger rounded-circle fs-24">
                          <i className="ri-error-warning-line" />
                        </div>
                      </div>

                      <h5>
                        Unable to load leads
                      </h5>

                      <p className="text-muted">
                        {error}
                      </p>

                      <Button
                        color="primary"
                        onClick={
                          fetchLeads
                        }
                      >
                        <i className="ri-refresh-line me-1" />
                        Retry
                      </Button>
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={
                        filteredLeads
                      }
                      isGlobalFilter={false}
                      isAddUserList={false}
                      customPageSize={10}
                      className="custom-header-css"
                      divClass="table-responsive table-card mb-3"
                      tableClass="align-middle table-nowrap"
                      theadClass="table-light"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* ==================================================
                RIGHT — LEAD DETAILS
            ================================================== */}

            <Col
              xl={4}
              lg={5}
            >
              <Card className="h-100">
                {!selectedLead ? (
                  <CardBody>
                    <div className="text-center py-5">
                      <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-1">
                          <i className="ri-user-search-line" />
                        </div>
                      </div>

                      <h5>
                        Select a Lead
                      </h5>

                      <p className="text-muted mb-0">
                        Select a lead from
                        the table to view
                        its details.
                      </p>
                    </div>
                  </CardBody>
                ) : detailsLoading ? (
                  <CardBody>
                    <div className="text-center py-5">
                      <Spinner
                        size="sm"
                        className="me-2"
                      />

                      Loading lead
                      details...
                    </div>
                  </CardBody>
                ) : (
                  <LeadDetailsCard
                    lead={
                      selectedLeadDetails?.lead ||
                      selectedLead
                    }
                    profile={
                      selectedLeadDetails?.profile
                    }
                    activities={
                      selectedLeadDetails?.activities ||
                      []
                    }
                    onEdit={() =>
                      handleEditLead(
                        selectedLeadDetails?.lead ||
                        selectedLead
                      )
                    }
                    onDelete={() =>
                      setDeleteModal(
                        true
                      )
                    }
                  />
                )}
              </Card>
            </Col>
          </Row>
        </Container>

        {/* ====================================================
            ADD / EDIT MODAL
        ==================================================== */}

        <Modal
          isOpen={modal}
          toggle={toggleModal}
          centered
          size="lg"
        >
          <ModalHeader
            className="bg-primary-subtle"
            toggle={toggleModal}
          >
            {isEdit
              ? "Edit Lead"
              : "Add Lead"}
          </ModalHeader>

          <Form
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }}
          >
            <ModalBody>
              <Row className="g-3">
                <Col lg={6}>
                  <Label>
                    Student Name{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </Label>

                  <Input
                    name="name"
                    value={
                      validation.values
                        .name
                    }
                    onChange={
                      validation.handleChange
                    }
                    onBlur={
                      validation.handleBlur
                    }
                    placeholder="Enter student name"
                    invalid={
                      validation.touched
                        .name &&
                      !!validation.errors
                        .name
                    }
                  />

                  <FormFeedback>
                    {
                      validation.errors
                        .name
                    }
                  </FormFeedback>
                </Col>

                <Col lg={6}>
                  <Label>
                    Phone{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </Label>

                  <Input
                    name="phone"
                    value={
                      validation.values
                        .phone
                    }
                    onChange={
                      validation.handleChange
                    }
                    onBlur={
                      validation.handleBlur
                    }
                    placeholder="Enter phone number"
                    invalid={
                      validation.touched
                        .phone &&
                      !!validation.errors
                        .phone
                    }
                  />

                  <FormFeedback>
                    {
                      validation.errors
                        .phone
                    }
                  </FormFeedback>
                </Col>

                <Col lg={6}>
                  <Label>
                    Email
                  </Label>

                  <Input
                    name="email"
                    type="email"
                    value={
                      validation.values
                        .email
                    }
                    onChange={
                      validation.handleChange
                    }
                    placeholder="Enter email address"
                  />
                </Col>

                <Col lg={6}>
                  <Label>
                    Alternate Phone
                  </Label>

                  <Input
                    name="alternatePhone"
                    value={
                      validation.values
                        .alternatePhone
                    }
                    onChange={
                      validation.handleChange
                    }
                    placeholder="Enter alternate phone"
                  />
                </Col>

                <Col lg={4}>
                  <Label>
                    Lead Source
                  </Label>

                  <Select
                    options={
                      sourceOptions
                    }
                    value={
                      sourceOptions.find(
                        (item) =>
                          item.value ===
                          validation
                            .values
                            .source
                      ) || null
                    }
                    onChange={(
                      option
                    ) =>
                      validation.setFieldValue(
                        "source",
                        option?.value ||
                        ""
                      )
                    }
                  />
                </Col>

                <Col lg={4}>
                  <Label>
                    Status
                  </Label>

                  <Select
                    options={
                      statusOptions
                    }
                    value={
                      statusOptions.find(
                        (item) =>
                          item.value ===
                          validation
                            .values
                            .status
                      ) || null
                    }
                    onChange={(
                      option
                    ) =>
                      validation.setFieldValue(
                        "status",
                        option?.value ||
                        ""
                      )
                    }
                  />
                </Col>

                <Col lg={4}>
                  <Label>
                    Priority
                  </Label>

                  <Select
                    options={
                      priorityOptions
                    }
                    value={
                      priorityOptions.find(
                        (item) =>
                          item.value ===
                          validation
                            .values
                            .priority
                      ) || null
                    }
                    onChange={(
                      option
                    ) =>
                      validation.setFieldValue(
                        "priority",
                        option?.value ||
                        ""
                      )
                    }
                  />
                </Col>

                <Col lg={12}>
                  <Label>
                    Notes
                  </Label>

                  <Input
                    name="notes"
                    type="textarea"
                    rows="4"
                    value={
                      validation.values
                        .notes
                    }
                    onChange={
                      validation.handleChange
                    }
                    placeholder="Enter lead notes..."
                  />
                </Col>
                {/* EDUCATION DETAILS */}
<Col lg={12}>
  <h6 className="text-primary border-bottom pb-2 mb-0">
    Education & Admission Details
  </h6>
</Col>


<Col lg={6}>
  <Label>Course Interested In</Label>

  <Select
    options={organizationCourses.map((course) => ({
      value: course.name,
      label: course.name,
    }))}
    value={
      organizationCourses
        .map((course) => ({
          value: course.name,
          label: course.name,
        }))
        .find(
          (option) => option.value === validation.values.course
        ) || (
          validation.values.course
            ? {
                value: validation.values.course,
                label: validation.values.course,
              }
            : null
        )
    }
    onChange={(option) => {
      const selectedCourse = option?.value || "";

      validation.setFieldValue("course", selectedCourse);

      const courseConfig = organizationCourses.find(
        (course) => course.name === selectedCourse
      );

      const streams = courseConfig?.streams || [];

      // Clear a stream that does not belong to the new course.
      if (!streams.includes(validation.values.program)) {
        validation.setFieldValue("program", "");
      }
    }}
    placeholder="Select course..."
    isClearable
  />
</Col>

<Col lg={6}>
  <Label>Stream / Program</Label>

  <Select
    options={(
      organizationCourses.find(
        (course) => course.name === validation.values.course
      )?.streams || []
    ).map((stream) => ({
      value: stream,
      label: stream,
    }))}
    value={
      (
        organizationCourses.find(
          (course) => course.name === validation.values.course
        )?.streams || []
      )
        .map((stream) => ({
          value: stream,
          label: stream,
        }))
        .find(
          (option) => option.value === validation.values.program
        ) || (
          validation.values.program
            ? {
                value: validation.values.program,
                label: validation.values.program,
              }
            : null
        )
    }
    onChange={(option) =>
      validation.setFieldValue("program", option?.value || "")
    }
    placeholder={
      validation.values.course
        ? "Select stream..."
        : "Select a course first"
    }
    isDisabled={!validation.values.course}
    isClearable
  />
</Col>


<Col lg={6}>
  <Label>Specialization</Label>
  <Input
    name="specialization"
    value={validation.values.specialization}
    onChange={validation.handleChange}
    placeholder="Enter specialization"
  />
</Col>

<Col lg={6}>
  <Label>Academic Year</Label>
  <Input
    name="academicYear"
    value={validation.values.academicYear}
    onChange={validation.handleChange}
    placeholder="e.g. 2027-28"
  />
</Col>

<Col lg={6}>
  <Label>Admission Type</Label>
  <Input
    name="admissionType"
    value={validation.values.admissionType}
    onChange={validation.handleChange}
    placeholder="e.g. Regular, Lateral Entry"
  />
</Col>

<Col lg={6}>
  <Label>Preferred Campus</Label>
  <Input
    name="preferredCampus"
    value={validation.values.preferredCampus}
    onChange={validation.handleChange}
    placeholder="Enter preferred campus"
  />
</Col>

{/* PARENT / GUARDIAN */}
<Col lg={12}>
  <h6 className="text-primary border-bottom pb-2 mb-0 mt-2">
    Parent / Guardian Details
  </h6>
</Col>

<Col lg={6}>
  <Label>Parent / Guardian Name</Label>
  <Input
    name="parentName"
    value={validation.values.parentName}
    onChange={validation.handleChange}
    placeholder="Enter parent name"
  />
</Col>

<Col lg={6}>
  <Label>Parent Contact Number</Label>
  <Input
    name="parentPhone"
    type="tel"
    value={validation.values.parentPhone}
    onChange={validation.handleChange}
    placeholder="Enter parent contact number"
  />
</Col>

<Col lg={6}>
  <Label>Parent Email</Label>
  <Input
    name="parentEmail"
    type="email"
    value={validation.values.parentEmail}
    onChange={validation.handleChange}
    placeholder="Enter parent email"
  />
</Col>

<Col lg={6}>
  <Label>Relationship</Label>
  <Input
    name="parentRelation"
    value={validation.values.parentRelation}
    onChange={validation.handleChange}
    placeholder="e.g. Father, Mother, Guardian"
  />
</Col>

{/* PREVIOUS EDUCATION */}
<Col lg={12}>
  <h6 className="text-primary border-bottom pb-2 mb-0 mt-2">
    Previous Education
  </h6>
</Col>

<Col lg={6}>
  <Label>Previous Qualification / Class</Label>
  <Input
    name="previousQualification"
    value={validation.values.previousQualification}
    onChange={validation.handleChange}
    placeholder="e.g. Class 12, B.Sc"
  />
</Col>

<Col lg={6}>
  <Label>Previous School / College</Label>
  <Input
    name="previousSchool"
    value={validation.values.previousSchool}
    onChange={validation.handleChange}
    placeholder="Enter school or college"
  />
</Col>

<Col lg={6}>
  <Label>Board / University</Label>
  <Input
    name="previousBoard"
    value={validation.values.previousBoard}
    onChange={validation.handleChange}
    placeholder="Enter board or university"
  />
</Col>

<Col lg={6}>
  <Label>Percentage / Marks</Label>
  <Input
    name="percentage"
    type="number"
    min="0"
    max="100"
    value={validation.values.percentage}
    onChange={validation.handleChange}
    placeholder="Enter percentage"
  />
</Col>
              </Row>
            </ModalBody>

            <ModalFooter>
              <Button
                color="light"
                onClick={
                  toggleModal
                }
              >
                Cancel
              </Button>

              <Button
                color="primary"
                type="submit"
              >
                {isEdit
                  ? "Update Lead"
                  : "Create Lead"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        {/* ====================================================
            DELETE
        ==================================================== */}

        <DeleteModal
          show={deleteModal}
          onDeleteClick={
            handleDeleteLead
          }
          onCloseClick={() =>
            setDeleteModal(false)
          }
        />

        <DeleteModal
          show={deleteModalMulti}
          onDeleteClick={
            deleteMultiple
          }
          onCloseClick={() =>
            setDeleteModalMulti(
              false
            )
          }
        />

        <ToastContainer
          closeButton={false}
          limit={1}
        />
      </div>
    </>
  );
};

/* ============================================================
   LEAD DETAILS CARD
============================================================ */

const LeadDetailsCard = ({
  lead,
  profile,
  activities,
  onEdit,
  onDelete,
}) => {
  if (!lead) {
    return null;
  }

  const assignedTo =
    lead.assignedTo;

  const course =
    profile?.course ||
    profile?.program ||
    profile?.specialization ||
    "-";

  return (
    <>
      {/* HEADER */}

      <CardHeader>
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div className="d-flex align-items-center">
            <div className="avatar-md me-3">
              <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-24">
                <i className="ri-user-line" />
              </div>
            </div>

            <div>
              <h5 className="mb-1">
                {getLeadName(
                  lead
                )}
              </h5>

              <div className="text-muted">
                {lead.leadNumber ||
                  "Lead"}
              </div>
            </div>
          </div>

          <UncontrolledDropdown>
            <DropdownToggle
              href="#"
              tag="button"
              className="btn btn-soft-secondary btn-sm"
              onClick={(e) =>
                e.preventDefault()
              }
            >
              <i className="ri-more-2-fill" />
            </DropdownToggle>

            <DropdownMenu end>
              <DropdownItem
                onClick={
                  onEdit
                }
              >
                <i className="ri-pencil-line me-2" />
                Edit Lead
              </DropdownItem>

              <DropdownItem
                onClick={
                  onDelete
                }
              >
                <i className="ri-delete-bin-line me-2 text-danger" />
                Delete Lead
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      </CardHeader>

      <CardBody
        style={{
          maxHeight:
            "calc(100vh - 260px)",
          overflowY: "auto",
        }}
      >
        {/* STATUS */}

        <div className="d-flex flex-wrap gap-2 mb-4">
          <span
            className={`badge ${getStatusClass(
              lead.status
            )}`}
          >
            {lead.status ||
              "No Status"}
          </span>

          <span
            className={`badge ${getPriorityClass(
              lead.priority
            )}`}
          >
            {lead.priority ||
              "No Priority"}
          </span>

          {lead.source && (
            <Badge
              color="primary"
              className="bg-primary-subtle text-primary"
            >
              {lead.source}
            </Badge>
          )}
        </div>

        {/* CONTACT */}

        <div className="mb-4">
          <h6 className="text-uppercase text-muted fs-12 mb-3">
            Contact Information
          </h6>

          <div className="border rounded p-3">
            <div className="d-flex align-items-center mb-3">
              <div className="avatar-xs me-3">
                <div className="avatar-title bg-light rounded-circle">
                  <i className="ri-phone-line text-muted" />
                </div>
              </div>

              <div>
                <div className="text-muted fs-12">
                  Phone
                </div>

                {lead.phone ? (
                  <a
                    href={`tel:${lead.phone}`}
                    className="text-body fw-medium"
                  >
                    {lead.phone}
                  </a>
                ) : (
                  "-"
                )}
              </div>
            </div>

            <div className="d-flex align-items-center">
              <div className="avatar-xs me-3">
                <div className="avatar-title bg-light rounded-circle">
                  <i className="ri-mail-line text-muted" />
                </div>
              </div>

              <div>
                <div className="text-muted fs-12">
                  Email
                </div>

                {lead.email ? (
                  <a
                    href={`mailto:${lead.email}`}
                    className="text-body fw-medium"
                  >
                    {lead.email}
                  </a>
                ) : (
                  "-"
                )}
              </div>
            </div>
          </div>
        </div>

        {/* LEAD INFORMATION */}

        <div className="mb-4">
          <h6 className="text-uppercase text-muted fs-12 mb-3">
            Lead Information
          </h6>

          <div className="row g-3">
            <div className="col-6">
              <div className="text-muted fs-12">
                Source
              </div>

              <div className="fw-medium">
                {lead.source ||
                  "-"}
              </div>
            </div>

            <div className="col-6">
              <div className="text-muted fs-12">
                Created
              </div>

              <div className="fw-medium">
                {formatDate(
                  lead.createdAt
                )}
              </div>
            </div>

            <div className="col-6">
              <div className="text-muted fs-12">
                Assigned To
              </div>

              <div className="fw-medium">
                {assignedTo
                  ? typeof assignedTo ===
                    "object"
                    ? assignedTo.name ||
                    assignedTo.email
                    : assignedTo
                  : "Unassigned"}
              </div>
            </div>

            <div className="col-6">
              <div className="text-muted fs-12">
                Lead Score
              </div>

              <div className="fw-medium">
                {lead.leadScore ??
                  "-"}
              </div>
            </div>

            <div className="col-12">
              <div className="text-muted fs-12">
                Next Follow-up
              </div>

              <div className="fw-medium">
                {formatDateTime(
                  lead.nextFollowUp
                )}
              </div>
            </div>
          </div>
        </div>

        {/* EDUCATION PROFILE */}

        <div className="mb-4">
          <h6 className="text-uppercase text-muted fs-12 mb-3">
            Education Profile
          </h6>

          <div className="border rounded p-3">
            <div className="row g-3">
              <div className="col-6">
                <div className="text-muted fs-12">
                  Student
                </div>

                <div className="fw-medium">
                  {profile?.studentName ||
                    getLeadName(
                      lead
                    )}
                </div>
              </div>

              <div className="col-6">
                <div className="text-muted fs-12">
                  Course / Program
                </div>

                <div className="fw-medium">
                  {course}
                </div>
              </div>

              <div className="col-6">
                <div className="text-muted fs-12">
                  Parent
                </div>

                <div className="fw-medium">
                  {profile?.parentName ||
                    "-"}
                </div>
              </div>

              <div className="col-6">
                <div className="text-muted fs-12">
                  Parent Phone
                </div>

                <div className="fw-medium">
                  {profile?.parentPhone ||
                    "-"}
                </div>
              </div>

              <div className="col-6">
                <div className="text-muted fs-12">
                  Previous School
                </div>

                <div className="fw-medium">
                  {profile?.previousSchool ||
                    "-"}
                </div>
              </div>

              <div className="col-6">
                <div className="text-muted fs-12">
                  Previous Board
                </div>

                <div className="fw-medium">
                  {profile?.previousBoard ||
                    "-"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* NOTES */}

        <div className="mb-4">
          <h6 className="text-uppercase text-muted fs-12 mb-3">
            Notes
          </h6>

          <div className="bg-light rounded p-3">
            {lead.notes ? (
              <div
                style={{
                  whiteSpace:
                    "pre-wrap",
                }}
              >
                {lead.notes}
              </div>
            ) : (
              <span className="text-muted">
                No notes added.
              </span>
            )}
          </div>
        </div>

        {/* ACTIVITY */}

        <div>
          <h6 className="text-uppercase text-muted fs-12 mb-3">
            Recent Activity
          </h6>

          {!activities ||
            activities.length ===
            0 ? (
            <div className="text-muted text-center py-3">
              No activity yet.
            </div>
          ) : (
            <div>
              {activities
                .slice(0, 8)
                .map(
                  (
                    activity,
                    index
                  ) => (
                    <div
                      className="d-flex mb-3"
                      key={
                        activity._id ||
                        index
                      }
                    >
                      <div className="flex-shrink-0">
                        <div className="avatar-xs">
                          <div className="avatar-title bg-primary-subtle text-primary rounded-circle">
                            <i className="ri-history-line" />
                          </div>
                        </div>
                      </div>

                      <div className="flex-grow-1 ms-3">
                        <div className="fw-medium">
                          {
                            activity.type
                          }
                        </div>

                        <div className="text-muted fs-13">
                          {
                            activity.description
                          }
                        </div>

                        <div className="text-muted fs-11 mt-1">
                          {formatDateTime(
                            activity.createdAt
                          )}
                        </div>
                      </div>
                    </div>
                  )
                )}
            </div>
          )}
        </div>
      </CardBody>
    </>
  );
};

export default AllLeads;